import { Controller } from "@hotwired/stimulus"

// Client-side preflight + drag-drop for the FormSubmission upload widget.
//
// Wiring (set on the form_with element):
//   data-controller="form-upload"
//
// Targets:
//   fileInput  – the <input type=file multiple> (also has data attributes
//                with existing-count / max-count / max-bytes for limits).
//   dropzone   – the desktop drag-drop area.
//   errorList  – an empty <ul hidden> the controller fills with preflight
//                errors. Hidden when empty so the layout doesn't shift.
//
// Server is authoritative — Step 2's model validators reject anything we
// miss. This is UX only: catch obvious mistakes BEFORE upload so users
// don't wait for a round trip.
//
// Mobile: drag-drop is desktop-only; tapping the file input opens the
// native iOS/Android picker (with camera capture via accept= + capture=).
const ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif"
])

export default class extends Controller {
  static targets = ["fileInput", "dropzone", "errorList"]

  connect() {
    if (this.hasDropzoneTarget) {
      this.boundDragOver = this.onDragOver.bind(this)
      this.boundDrop     = this.onDrop.bind(this)
      this.dropzoneTarget.addEventListener("dragover", this.boundDragOver)
      this.dropzoneTarget.addEventListener("dragleave", this.onDragLeave.bind(this))
      this.dropzoneTarget.addEventListener("drop", this.boundDrop)
      // Tap the dropzone → open the file picker (parity with the label).
      this.dropzoneTarget.addEventListener("click", () => this.fileInputTarget.click())
    }
    if (this.hasFileInputTarget) {
      this.fileInputTarget.addEventListener("change", () => this.preflight())
    }
    // Block submit if preflight errors are showing.
    this.element.addEventListener("submit", (e) => {
      if (!this.preflight()) e.preventDefault()
    })
  }

  onDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.style.borderColor = "var(--color-primary)"
  }

  onDragLeave() {
    this.dropzoneTarget.style.borderColor = "var(--color-border)"
  }

  onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.style.borderColor = "var(--color-border)"
    if (!event.dataTransfer?.files?.length) return
    // DataTransferItemList → DataTransfer → FileList. Browsers since ~2018
    // let us assign FileList directly to <input type=file>.files.
    const dt = new DataTransfer()
    for (const file of event.dataTransfer.files) dt.items.add(file)
    this.fileInputTarget.files = dt.files
    this.preflight()
  }

  // Returns true if all picked files pass preflight. Side effect: writes
  // to errorList target. Per-file progress (XHR progress event) — TODO V2.1;
  // would require swapping the form_with submit for a manual XHR.
  preflight() {
    const errors = []
    const input  = this.fileInputTarget
    const files  = Array.from(input.files || [])
    const existing = parseInt(input.dataset.existingCount || "0", 10)
    const maxCount = parseInt(input.dataset.maxCount || "10", 10)
    const maxBytes = parseInt(input.dataset.maxBytes || `${25 * 1024 * 1024}`, 10)

    if (existing + files.length > maxCount) {
      errors.push(`Too many files. You can attach at most ${maxCount} (${existing} already uploaded).`)
    }

    for (const file of files) {
      if (file.size > maxBytes) {
        errors.push(`${file.name}: too large (max 25 MB).`)
      }
      // file.type is "" for some HEIC files on iOS — defer to the server's
      // analyzed content_type rather than blocking. Only reject types we
      // KNOW are wrong.
      if (file.type && !ALLOWED_TYPES.has(file.type)) {
        errors.push(`${file.name}: unsupported file type.`)
      }
    }

    this.renderErrors(errors)
    return errors.length === 0
  }

  renderErrors(errors) {
    if (!this.hasErrorListTarget) return
    const list = this.errorListTarget
    list.innerHTML = ""
    if (errors.length === 0) {
      list.hidden = true
      return
    }
    for (const msg of errors) {
      const li = document.createElement("li")
      li.textContent = msg  // textContent, not innerHTML — never trust input.
      list.appendChild(li)
    }
    list.hidden = false
  }
}
